/** Shared pieces of the v5 order screens, copied from Omni's own components (PageHeader, cards, pills, inputs). */
import { useContext, useEffect, useState } from 'react';
import { StepsSlot } from './styles';
import { cn } from '@/components/ui/utils';
import { PageHeader } from '@/components/PageHeader';

export const H1 = ({ title, sub }: { title: string; sub?: string }) => (
  <PageHeader category="NEW CAMPAIGN" title={title} subtitle={sub} actions={useContext(StepsSlot)} />
);

export const H2 = ({ children, note }: { children: React.ReactNode; note?: React.ReactNode }) => (
  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
    <h2 className="text-[16px] leading-tight text-[#101828]" style={{ fontWeight: 600 }}>{children}</h2>
    {note && <span className="text-[12px] text-[#667085]">{note}</span>}
  </div>
);

export const Card = ({ children, className, tone }: { children: React.ReactNode; className?: string; tone?: 'soft' | 'dark' }) => (
  <div className={cn('rounded-2xl border p-4 sm:p-5', tone === 'dark' ? 'border-transparent bg-[#2e024c] text-white' : tone === 'soft' ? 'border-[#be43ff]/10 bg-[#f8f5ff]' : 'border-gray-100 bg-white shadow-sm', className)}>{children}</div>
);

/** A row of choices where one is on; dark pill = chosen. */
export function Pills<T extends string>({ value, options, onChange, label }: { value: T | null; options: Array<{ id: T; label: string; title?: string }>; onChange: (v: T) => void; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button key={o.id} type="button" role="radio" aria-checked={value === o.id} title={o.title} onClick={() => onChange(o.id)}
          className={cn('min-h-[32px] rounded-full border px-3 text-[13px] transition-colors', value === o.id ? 'border-[#2e024c] bg-[#2e024c] text-white' : 'border-gray-200 bg-white text-[#344054] hover:border-[#be43ff]/40 hover:bg-[#fcf7ff]')}
          style={{ fontWeight: value === o.id ? 600 : 500 }}>{o.label}</button>
      ))}
    </div>
  );
}

/** Free text saved when the field is left or Enter is pressed (never per keystroke). */
export function TextCommit({ id, label, value, placeholder, onCommit, area, hint }: { id: string; label?: string; value: string; placeholder?: string; onCommit: (v: string) => void; area?: boolean; hint?: string }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => { if (draft.trim() !== value.trim()) onCommit(draft.trim()); };
  const cls = 'w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-[#101828] placeholder:text-[#98a2b3] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30 focus:border-[#be43ff] transition-all';
  return (
    <label htmlFor={id} className="flex flex-col gap-1.5">
      {label && <span className="text-[12.5px] text-[#344054]" style={{ fontWeight: 600 }}>{label}</span>}
      {area
        ? <textarea id={id} rows={3} value={draft} placeholder={placeholder} onChange={(e) => setDraft(e.target.value)} onBlur={commit} className={cn(cls, 'resize-y leading-relaxed')} />
        : <input id={id} value={draft} placeholder={placeholder} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} className={cls} />}
      {hint && <span className="text-[12px] text-[#667085]">{hint}</span>}
    </label>
  );
}

